import React, { Component } from "react";
import { ReactCookieProps, withCookies } from "react-cookie";
import base64 from "base-64";
import { Form, Button } from "semantic-ui-react";
import * as Sentry from "@sentry/nextjs";
import _ from "lodash";
import { Props } from "../propTypes";
import { childFormComponent } from "./types";
import { ObjectLiteral } from "../../constants";
import classnames from "classnames";
import { ResetForm } from "./ResetForm";

// Email Form Component
/**
 * This component is used to input and store a user's email information in MyElevation.
 *
 * We are using the Form and Button components from Semantic UI React Library.
 * For more information on these components can be found at https://react.semantic-ui.com/collections/form/
 * and https://react.semantic-ui.com/elements/button/
 *
 * Utf8 and base64 for text-encoding. More information about these can be found at
 * https://www.npmjs.com/package/base-64/ and https://www.npmjs.com/package/utf8/
 *
 * React Cookie is being used to set a cookie on the user's browser for 30 minutes. More information about this
 * can be found at https://www.npmjs.com/package/react-cookie/
 */

type State = {
    valid: boolean;
    showValidation: boolean;
    validationText: string;
    emailValue: string;
};

class Email extends Component<
    Props & childFormComponent & ReactCookieProps,
    State
> {
    state = {
        valid: false,
        showValidation: false,
        validationText: "",
        emailValue: "",
    };

    // Component Did Update Lifecycle Method
    /**
     * componentDidUpdate() runs immediately after the component updates.
     * This method does not run during the initial render.
     *
     * In this instance, we are validating the code entered by the user. If there is an error, we return a message.
     * If valid, we send the user to the next step.
     */
    componentDidUpdate(
        prevProps: Props & childFormComponent & ReactCookieProps
    ) {
        if (
            this.props.main.confirmationCodeData &&
            this.props.main.confirmationCodeData.status_code &&
            this.props.main.confirmationCodeData.status_code !== 200 &&
            prevProps.main.confirmationCodeData &&
            prevProps.main.confirmationCodeData.status_code !==
                this.props.main.confirmationCodeData.status_code
        ) {
            let errorText =
                "Error sending the confirmation code. Please try again later.";

            if (
                this.props.main.confirmationCodeData.message &&
                _.isArray(this.props.main.confirmationCodeData.message) &&
                this.props.main.confirmationCodeData.message[0] &&
                _.isObject(this.props.main.confirmationCodeData.message[0])
            ) {
                errorText =
                    this.props.main.confirmationCodeData &&
                    this.props.main.confirmationCodeData.messages &&
                    (
                        this.props.main.confirmationCodeData
                            .message as ObjectLiteral[]
                    )[0][
                        Object.keys(
                            this.props.main.confirmationCodeData.message[0]
                        )[0]
                    ];
            }

            const extraErrorData = {
                status_code: this.props.main.confirmationCodeData.status_code,
                other_validation_data: this.props.main.confirmationCodeData,
            };

            if (this.props.main.confirmationCodeData.origin !== "USER") {
                this.props.logError &&
                    this.props.logError(
                        errorText,
                        Sentry.Severity.Fatal,
                        extraErrorData
                    );
            }

            this.setState({
                showValidation: true,
                validationText: errorText,
            });
        }

        if (
            this.props.main.confirmationCodeData &&
            this.props.main.confirmationCodeData.status_code === 200 &&
            prevProps.main.confirmationCodeData &&
            prevProps.main.confirmationCodeData.status_code !==
                this.props.main.confirmationCodeData.status_code
        ) {
            this.props.nextStep(2);
        }
    }

    // Save and Continue Function
    /**
     * saveAndContinue() saves the values to the state and goes to the corresponding next step.
     * In this instance, we are checking to make sure the code is valid and sending user to the corresponding step.
     */
    saveAndContinue = (e: any) => {
        e.preventDefault();
        if (this.state.valid) {
            const { cookies, values } = this.props;
            let content = "";

            if (cookies && this.props.registrationType) {
                cookies.get(
                    "confirmation_" + this.props.registrationType.toLowerCase()
                );
            }

            let contentSplit: Array<any> | null = null;
            let isTheSameEmail = false;
            if (content && this.props.saveSession) {
                const contentDecode = base64.decode(content);
                contentSplit = contentDecode.split("+++");
            }

            if (content && contentSplit && contentSplit.length === 2) {
                if (contentSplit[0]) {
                    const decodedData = JSON.parse(contentSplit[0]);

                    if (
                        decodedData &&
                        decodedData.type === "email" &&
                        decodedData.data &&
                        decodedData.data.email === values.emailConfirmation
                    ) {
                        isTheSameEmail = true;
                    }
                }
            }

            if (
                content &&
                contentSplit &&
                contentSplit.length === 2 &&
                isTheSameEmail
            ) {
                if (contentSplit[1]) {
                    this.props.handleChange("fromSalesForce")(null, true);
                    this.props.actions.main.main_set_user_data(
                        JSON.parse(contentSplit[1])
                    );
                    this.props.nextStep(6);
                } else {
                    this.props.nextStep(3);
                }
            } else {
                // this.props.handleChange('phoneConfirmationFormat')(this.state.formatData)
                this.props.actions.main.main_send_confirmation_code({
                    dataToSend: { sms: false, email: values.emailConfirmation },
                    origin: this.props.registrationType,
                });
            }
        } else {
            this.setState({
                showValidation: true,
                validationText: "Invalid email",
            });
        }
    };

    // On Email Change Function
    /**
     * onChangeEmail() handles the value of the email input and checks to make sure it's valid.
     */
    onChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
        const regex = RegExp(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i);
        const valid = regex.test(event.target.value.trim());

        this.setState({
            valid,
            showValidation: false,
            emailValue: event.target.value.trim(),
        });

        this.props.handleChange("emailConfirmation")(event);
    };

    render() {
        const { values } = this.props;
        return (
            <>
                <div className="col-xs-12 max-w-500 full-width center-content m-b-spacer-3xl">
                    <Form
                        className={`p-y-spacer-xl  ${
                            this.state.showValidation ? "ec-error" : ""
                        }`}
                    >
                        <Form.Field
                            className={classnames(
                                "label-animation-input rel-pos flex flex-column full-width m-b-spacer-lg ",
                                {
                                    filled: this.state.emailValue,
                                }
                            )}
                        >
                            <label>Email</label>
                            <input
                                className="p-x-spacer-lg no-border ec-border-radius-sm b-width-xs  border-trans-light-grey-alt text-alt-dark-grey full-width"
                                onChange={this.onChangeEmail}
                                value={this.state.emailValue}
                                autoFocus={true}
                            />
                            {this.state.showValidation ? (
                                <small className="ec-primary-font ec-line-height">
                                    {this.state.validationText}
                                </small>
                            ) : null}
                        </Form.Field>

                        {this.props.isLoading ? (
                            <div className="form-loading rel-pos  m-t-spacer-md  way-forward full-width uptop">
                                <div className="la-ball-clip-rotate la-color la-md loading-spin abs-pos absolute-center">
                                    <div />
                                </div>
                            </div>
                        ) : (
                            <Button
                                id={`${this.props.tagPrefixId}-email-next`}
                                className="full-width no-border text-white m-t-spacer-md p-y-spacer-md uppercase let-sp-reg hover-grow ec-border-radius-sm"
                                disabled={
                                    !values.emailConfirmation ||
                                    this.props.isLoading
                                }
                                onClick={this.saveAndContinue}
                            >
                                Next
                            </Button>
                        )}
                    </Form>
                    {/* <div className="m-t-spacer-lg text-center">
                        <small className="regular">
                            <span className="text-alt-dark-grey half-here">
                                Prefer to receive a text message?
                            </span>{" "}
                            <Button
                                id={`${this.props.tagPrefixId}-email-send-sms`}
                                onClick={() => {
                                    this.props.phoneOrEmail &&
                                        this.props.phoneOrEmail();
                                }}
                                className="ec-no-background no-border bold help-text hover-grow"
                            >
                                Send text message
                            </Button>
                        </small>
                    </div> */}
                </div>
                <ResetForm
                    idPrefix={`${this.props.tagPrefixId}-email`}
                    showReset={false}
                    cancelUrl={this.props.cancelUrl || ""}
                />
            </>
        );
    }
}

export default withCookies(Email);
